/**
 * ChatComposer — V80 input row (M5.3).
 *
 * Textarea + attach + send. Auto-grows on input, Cmd/Ctrl+Enter or
 * Enter (no-shift) sends. Drag-drop and paste-image targets. Fires
 * `chatStore.dispatchMessage` which handles optimistic push + POST.
 *
 * On 401: draft is preserved and `onTokenRejected` fires so the host
 * page can pop the M6.2 unlock modal. On `daemonStore.outdated`: send
 * is refused and `onDaemonOutdated` fires so V47 can take over.
 *
 * Behaviour is parity with the V80 monolith; everything outside the
 * input row (Stop button, scope strip) is the parent's concern.
 */

import { For, Show, createEffect, createMemo, createSignal, onCleanup, onMount } from 'solid-js';
import { chatStore, ONBOARDING_CONV_ID } from '~/state/chat';
import { daemonStore } from '~/state/daemon';
import { isProjectEmpty } from '~/state/server';
import { onboardingBootstrapBrief } from '~/lib/onboarding-brief';
import { isConvWorking as convIsWorking } from '~/state/live-selectors';
import { editQueuedItem, deleteQueuedItem, moveQueuedItem } from '~/lib/queue-actions';
import { useComposerDraft } from '~/components/chat/use-composer-draft';
import { isValidationRed } from '~/components/architect/ValidationBlock';
import { debugEmit } from '~/lib/debug-transport';
import { log } from '~/lib/log';
import { fitAttached, shrinkImage } from '~/lib/image-shrink';
import type { ChatQueueItem } from '~/lib/daemon-client';
import StopButton from '~/components/chat/strip/StopButton';
import AttachmentGrid from '~/components/chat/bubbles/AttachmentGrid';

const ACCEPT = 'image/*,.md,.txt,.pdf,.json,.yaml,.yml,.csv,.log';
const MAX_IMAGES = 6;

// V83 — drop the `client` prop. Read the current DaemonClient reactively
// from daemonStore so dispatching follows project hot-swaps without the
// parent having to feed a fresh client down.
// V107.41 — Standard v16 chat-turn queue. The project::conv last hydrated,
// so the composer round-trips once per focus; WS `queue.*` events keep the
// store fresh in between.
let queueHydrated = '';

export default function ChatComposer(props: {
  conv: string;
  placeholder?: string;
  onTokenRejected?: (draft: string) => void;
  onDaemonOutdated?: () => void;
}) {
  const [sending, setSending] = createSignal(false);
  /** ATT2 — a plain-words warning above the input (images not kept). */
  const [notice, setNotice] = createSignal<string | null>(null);
  let fileEl: HTMLInputElement | undefined;
  let taEl: HTMLTextAreaElement | undefined;

  const grow = () => {
    if (!taEl) return;
    taEl.style.height = 'auto';
    // 2026-06-10 — operator field report: 180 px (≈3 lines) was too
    // tight while the agent is mid-stream. Bumped to 300 px (≈5-6
    // lines) so the operator can read what they're typing without
    // scrolling. Beyond that height the textarea overflows internally.
    taEl.style.height = Math.min(taEl.scrollHeight, 300) + 'px';
  };

  const { draft, setDraft, imgs, setImgs, docs, setDocs } = useComposerDraft(() => props.conv, grow);

  // PASTE1 — a remount drops focus mid-paste; record when it happens.
  onMount(() => debugEmit('ux.composer', 'composer mounted', { conv: props.conv, lvl: 'debug' }));
  onCleanup(() => debugEmit('ux.composer', 'composer unmounted', { conv: props.conv, lvl: 'debug' }));

  // V107.41 — Hydrate queue on focus (lazy fetch). QISO1 — once per
  // project + conv per visit: the same conv id exists in every project,
  // and a switch-back must re-read the daemon, not trust the cached copy.
  createEffect(() => {
    const conv = props.conv;
    const c = daemonStore.state.client;
    const key = `${c?.transport.projectId ?? ''}::${conv}`;
    if (!c || queueHydrated === key) return;
    queueHydrated = key;
    void chatStore.hydrateQueue(c, conv);
  });

  // Derived queue + working flags for the active conv.
  const queueItems = createMemo<ChatQueueItem[]>(
    () => chatStore.state.queues[props.conv] ?? [],
  );
  // Shared with the roadmap queue bar and the scope strip. The local
  // copy this replaced checked only the daemon's live/coordinating
  // flags, so between a send and the first snapshot the queue button
  // vanished while the chat already showed the turn running.
  const isConvWorking = createMemo<boolean>(() => convIsWorking(props.conv));
  // The queue-button appears only when the conv is busy — otherwise
  // the operator can just send normally with play (per the operator's
  // brief: "el reloj sólo aparece cuando ya tenemos el chat en marcha").
  // Plus: while queued items already exist, keep the button visible
  // even after the conv goes idle briefly between turns, so the operator
  // can append to a still-flushing queue.
  const showQueueBtn = createMemo<boolean>(
    () => isConvWorking() || queueItems().length > 0,
  );

  // Clock-icon enqueue = POST-WIP queue. Each click creates a NEW
  // separate item that will run AFTER the current turn finishes
  // (sequenced behind any items already in the queue). Items are
  // independent — operator can edit, delete, or move each one.
  // 2026-06-10 operator clarification: "se coloca como otra tarea
  // independiente en esa post queue."
  const enqueue = async (): Promise<void> => {
    const text = draft().trim();
    const pending = imgs();
    if (sending() || (!text && pending.length === 0)) return;
    const cli = daemonStore.state.client;
    if (!cli) return;
    setSending(true);
    // ATT1 — a queued message keeps its images (they used to be dropped).
    const queuedImgs = await Promise.all(pending.map(fitAttached));
    setDraft(''); setImgs([]); setDocs([]); grow();
    const res = await cli.queueEnqueue(props.conv, text, queuedImgs);
    setSending(false);
    // ATT2 — a daemon that predates queued attachments (< py-1.42.1)
    // accepts the text and silently drops the images. Never lose them:
    // put them back in the box and say so.
    if (res.ok && queuedImgs.length > (res.data.item?.attachments?.length ?? 0)) {
      setImgs(pending);
      setNotice('The images were not queued — the daemon has not updated yet. They are back in the box: send them now, or queue them again after the update.');
    }
    if (!res.ok) {
      setDraft(text); setImgs(pending); grow();
      log.warn('queue enqueue failed', { status: res.status });
      if (res.status === 401) props.onTokenRejected?.(text);
    } else {
      taEl?.focus();
      // Daemon broadcasts queue.item.added; ingestQueueEvent lands it.
    }
  };
  const editQueued = (id: string, text: string) => editQueuedItem(props.conv, id, text);
  const deleteQueued = (id: string) => deleteQueuedItem(props.conv, id);
  const moveQueued = (id: string, position: number) => moveQueuedItem(props.conv, id, position);

  const addFile = (file: File) => {
    if ((file.type || '').startsWith('image/')) {
      if (imgs().length >= MAX_IMAGES) return;
      // IMG1 — re-encode to fit the daemon's 4 MB body cap (inline base64).
      void shrinkImage(file).then((img) => {
        if (imgs().length < MAX_IMAGES) setImgs((xs) => [...xs, img]);
      });
    } else {
      const r = new FileReader();
      r.onload = (e) => setDocs((xs) => [...xs, { filename: file.name, content: String(e.target?.result ?? '') }]);
      r.readAsText(file);
    }
  };

  const send = async () => {
    const text = draft().trim();
    if (sending() || (!text && imgs().length === 0 && docs().length === 0)) return;
    if (daemonStore.state.outdated) { props.onDaemonOutdated?.(); return; }
    const cli = daemonStore.state.client;
    if (!cli) return;
    // Enter / play always goes through /chat/dispatch. When the conv
    // is live the daemon takes the busy-branch and appends to the
    // IN-TASK pending list (chat_sessions.queue) — that merges into
    // the current turn's continuation, not into the separate disk
    // queue. The clock-icon button is for the post-WIP disk queue
    // (case 2) instead. Operator clarification 2026-06-10.
    setSending(true);
    // IMG1 — anything still over budget (restored after a 413) is shrunk now.
    const sentImgs = await Promise.all(imgs().map(fitAttached));
    const sentDocs = docs();
    const contextDocs = [...sentDocs];
    if (
      props.conv === ONBOARDING_CONV_ID &&
      isProjectEmpty() &&
      !chatStore.onboardingHasUserMessages()
    ) {
      contextDocs.unshift({ filename: 'meshkore_coordinator_bootstrap.md', content: onboardingBootstrapBrief() });
    }
    // V50 — debug-stream marker. If the last assistant message is a
    // VALIDATION RED block, classify what the operator just submitted
    // (proceed / rework / free-form) so /debug/tail shows how the
    // validation gate was answered.
    const msgsForConv = chatStore.state.convMap[props.conv] ?? [];
    for (let i = msgsForConv.length - 1; i >= 0; i--) {
      const m = msgsForConv[i];
      if (!m || m.kind !== 'assistant') continue;
      if (isValidationRed(m.text ?? '')) {
        const lower = text.toLowerCase();
        const action = lower === 'proceed' ? 'proceed' : lower === 'rework' ? 'rework' : 'free-form';
        debugEmit('ux.validation', `Validation RED answered: ${action}`, {
          conv: props.conv,
          data: { action, chars: text.length },
        });
      }
      break;
    }
    setDraft(''); setImgs([]); setDocs([]); grow();
    const res = await chatStore.dispatchMessage(cli, {
      conv: props.conv, text, author: 'architect', images: sentImgs, contextDocs,
    });
    setSending(false);
    if (!res.ok) {
      setDraft(text); setImgs(sentImgs); setDocs(sentDocs); grow();
      if (res.status === 401) props.onTokenRejected?.(text);
    } else {
      taEl?.focus();
    }
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault(); void send();
  };

  // PASTE1 (2026-10-24) — "I have to paste two or three times". A copy
  // from some apps carries the text AND a file (a rendered image, an
  // attachment); the file branch called preventDefault, so the text never
  // landed. Text always pastes; a file is attached only when the clipboard
  // has no text (a screenshot). Each paste is logged with what it carried
  // and whether the text reached the box, so the next miss is visible.
  const onPaste = (e: ClipboardEvent) => {
    const dt = e.clipboardData;
    const items = dt?.items;
    const text = dt?.getData('text/plain') ?? '';
    const kinds: string[] = [];
    let files = 0;
    if (items) {
      for (let i = 0; i < items.length; i += 1) {
        const it = items[i]!;
        kinds.push(`${it.kind}:${it.type}`);
        if (it.kind === 'file' && !text) {
          const f = it.getAsFile();
          if (f) { e.preventDefault(); addFile(f); files += 1; }
        }
      }
    }
    const before = taEl?.value.length ?? 0;
    const focused = document.activeElement === taEl;
    // After the browser's own insertion (a microtask can run before it).
    setTimeout(() => {
      const after = taEl?.value.length ?? 0;
      debugEmit('ux.paste', text && after <= before ? 'paste did not reach the box' : 'paste', {
        conv: props.conv,
        lvl: text && after <= before ? 'warn' : 'info',
        data: { kinds, text_chars: text.length, files, before, after, focused, disabled: sending() },
      });
    }, 50);
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    const fs = e.dataTransfer?.files; if (!fs) return;
    for (let i = 0; i < fs.length; i += 1) addFile(fs[i]!);
  };

  return (
    <div class="flex flex-col gap-1.5 p-3 border-t border-gray-800/60"
      onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
      {/* ATT4 — the queue sits on top; what you are about to send (the
          attachments and any notice about them) sits right on the input.
          V107.41 — Queued turns (Standard v16). Renders above the input
          when there are items waiting. Max 30vh, scrolls overflow.
          Each row: drag handle (left) + editable text + ✕ delete. */}
      <Show when={queueItems().length > 0}>
        <QueuePanel
          items={queueItems()}
          onEdit={(id, text) => void editQueued(id, text)}
          onDelete={(id) => void deleteQueued(id)}
          onMove={(id, position) => void moveQueued(id, position)}
        />
      </Show>
      <Show when={imgs().length || docs().length}>
        <div class="flex flex-wrap gap-1.5">
          <For each={imgs()}>{(img, i) => (
            <div class="relative w-12 h-12 rounded border border-gray-800 overflow-hidden">
              <img src={img.dataURL} class="w-full h-full object-cover" />
              <button type="button" class="absolute top-0 right-0 w-4 h-4 bg-gray-950/80 text-gray-300 text-[10px] leading-none"
                onClick={() => setImgs((xs) => xs.filter((_, j) => j !== i()))}>✕</button>
            </div>
          )}</For>
          <For each={docs()}>{(d, i) => (
            <div class="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-900 border border-gray-800 text-[11px] text-gray-300">
              <span class="font-mono truncate max-w-[160px]">📄 {d.filename}</span>
              <button type="button" class="text-gray-500 hover:text-red-300"
                onClick={() => setDocs((xs) => xs.filter((_, j) => j !== i()))}>✕</button>
            </div>
          )}</For>
        </div>
      </Show>
      <Show when={notice()}>
        {(n) => (
          <div class="flex items-start gap-2 px-2 py-1.5 rounded border border-amber-500/40 bg-amber-500/10 text-[12px] text-amber-200">
            <span class="flex-1">{n()}</span>
            <button type="button" class="text-amber-300/70 hover:text-amber-100" onClick={() => setNotice(null)}>✕</button>
          </div>
        )}
      </Show>
      {/* V86n — Recovered the textarea width: send + attach are now
          chrome-less icon buttons stacked vertically to the right.
          Send is a filled triangle on top (primary action, draws the
          eye), attach is the paperclip below. Both inherit the
          textarea's vertical bounds so they sit flush with its
          rounded corners. Textarea border lifted from gray-800 → 600
          so the operator can actually see where the box ends. */}
      <div class="flex gap-2 items-stretch">
        <textarea
          ref={taEl} value={draft()} rows="3" disabled={sending()}
          placeholder={props.placeholder ?? 'Reply…'}
          onInput={(e) => { setDraft(e.currentTarget.value); grow(); }}
          onKeyDown={onKey} onPaste={onPaste}
          class="flex-1 bg-gray-950 border border-gray-600/70 rounded-md px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-emerald-500/70 disabled:opacity-60 resize-none"
        />
        <input ref={fileEl} type="file" multiple accept={ACCEPT} class="hidden"
          onChange={(e) => { const fs = e.currentTarget.files; if (fs) for (let i = 0; i < fs.length; i += 1) addFile(fs[i]!); e.currentTarget.value = ''; }} />
        {/* V107.41 — Three-button stack: send (top), queue (middle,
            only when the conv is busy), attach (bottom). Tightened
            vertical spacing so the trio fits inside the textarea
            bounds without overflowing — `gap-1` distributes the
            three icons evenly. */}
        <div class="flex flex-col flex-shrink-0 py-0.5 gap-1">
          <button type="button" title="Send (Cmd/Ctrl+Enter)" onClick={() => void send()}
            disabled={sending() || (!draft().trim() && imgs().length === 0 && docs().length === 0)}
            class="inline-flex items-center justify-center w-8 h-8 rounded text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent">
            <Show
              when={!sending()}
              fallback={
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" class="animate-spin">
                  <path d="M21 12a9 9 0 11-6.219-8.56" stroke-linecap="round" />
                </svg>
              }
            >
              {/* Filled right-pointing triangle — send. */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M5 4l16 8-16 8V4z" />
              </svg>
            </Show>
          </button>
          {/* V107.41 — Queue button. Only shown when the conv is
              actively working (live or coordinating) OR there are
              already queued items waiting. Same content-validation
              as send, but POSTs to the queue endpoint instead of
              dispatch. Standard v16. */}
          <Show when={showQueueBtn()}>
            <button type="button" title="Queue for after current turn" onClick={() => void enqueue()}
              disabled={sending() || (!draft().trim() && imgs().length === 0)}
              class="inline-flex items-center justify-center w-8 h-8 rounded text-sky-300 hover:text-sky-200 hover:bg-sky-500/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent">
              {/* Clock icon. */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
            </button>
          </Show>
          <button type="button" title="Attach images or docs" onClick={() => fileEl?.click()}
            class="inline-flex items-center justify-center w-8 h-8 rounded text-gray-400 hover:text-gray-200 hover:bg-gray-800/60 transition-colors">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
        </div>
      </div>
      {/* ES-9 — STOP where the operator is looking: bottom-right, under the
          box they type in, while the agent works. */}
      <Show when={isConvWorking()}>
        <div class="flex justify-end">
          <StopButton conv={props.conv} />
        </div>
      </Show>
    </div>
  );
}

/**
 * V107.41 — Queue panel rendered above the composer when there are
 * items waiting. Each row: drag handle (≡), text (click to edit), ✕.
 * max-h: 30vh, scroll on overflow. Native HTML5 drag/drop for reorder.
 *
 * The daemon is the source of truth — every action POSTs and we let
 * the WS event update the store, so the panel "follows" the server.
 * Optimistic UI would require a rollback layer we don't need yet.
 */
function QueuePanel(props: {
  items: ChatQueueItem[];
  onEdit: (id: string, text: string) => void;
  onDelete: (id: string) => void;
  onMove: (id: string, position: number) => void;
}) {
  const [editingId, setEditingId] = createSignal<string | null>(null);
  const [editValue, setEditValue] = createSignal('');
  const [dragId, setDragId] = createSignal<string | null>(null);
  const [collapsed, setCollapsed] = createSignal<boolean>(true);
  // Auto-expand when NEW items arrive so the ✕/edit controls are
  // visible; never fights a manual collapse (only opens on growth).
  let prevCount = props.items.length;
  createEffect(() => {
    const n = props.items.length;
    if (n > prevCount) setCollapsed(false);
    prevCount = n;
  });

  const beginEdit = (it: ChatQueueItem): void => {
    setEditingId(it.id);
    setEditValue(it.text);
  };
  const commitEdit = (): void => {
    const id = editingId();
    if (!id) return;
    const next = editValue().trim();
    setEditingId(null);
    if (next && next !== props.items.find((it) => it.id === id)?.text) {
      props.onEdit(id, next);
    }
  };
  const cancelEdit = (): void => { setEditingId(null); };

  const onDragStart = (e: DragEvent, id: string): void => {
    setDragId(id);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', id);
    }
  };
  const onDragOver = (e: DragEvent): void => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
  };
  const onDrop = (targetIdx: number): void => {
    const src = dragId();
    setDragId(null);
    if (!src) return;
    const srcIdx = props.items.findIndex((it) => it.id === src);
    if (srcIdx < 0 || srcIdx === targetIdx) return;
    // Daemon move endpoint expects the FINAL position index.
    props.onMove(src, targetIdx);
  };

  return (
    <div class="border border-gray-800/70 bg-gray-950/40 rounded-md flex flex-col" style={{ 'max-height': '40vh' }}>
      {/* Header sits at top with the live count + chevron. List grows
       *  downward toward the textarea; because the whole panel is
       *  anchored above the input, the panel as a whole expands UPWARD
       *  into the chat area as items accumulate (max-height 40vh caps it). */}
      <div
        class={`flex-shrink-0 flex items-center gap-2 px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider text-sky-300/80 ${collapsed() ? '' : 'border-b border-gray-800/60'}`}
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
          <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" stroke-linecap="round" />
        </svg>
        <span>Queue · {props.items.length} waiting</span>
        <Show when={!collapsed()}>
          <span class="text-gray-500 normal-case tracking-normal font-sans">— runs after the current turn</span>
        </Show>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          class="ml-auto text-gray-500 hover:text-sky-300 px-1.5 py-0.5 leading-none rounded hover:bg-gray-900/60"
          title={collapsed() ? 'Expand queue' : 'Collapse queue'}
          aria-label={collapsed() ? 'expand queue' : 'collapse queue'}
          aria-expanded={!collapsed()}
        >
          {/* ▾ collapsed → click to expand (chevron points down toward list)
           *  ▴ expanded  → click to collapse (chevron points up away from list) */}
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
            <Show when={collapsed()} fallback={<path d="M6 15l6-6 6 6" />}>
              <path d="M6 9l6 6 6-6" />
            </Show>
          </svg>
        </button>
      </div>
      <ul class={`divide-y divide-gray-800/40 overflow-y-auto ${collapsed() ? 'hidden' : ''}`}>
        <For each={props.items}>
          {(it, i) => (
            <li
              class={`group flex items-start gap-2 px-2 py-1.5 text-[12.5px] ${dragId() === it.id ? 'opacity-50' : ''}`}
              draggable={editingId() !== it.id}
              onDragStart={(e) => onDragStart(e, it.id)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(i())}
            >
              {/* Drag handle — the operator's grab affordance. */}
              <span
                class="select-none flex-shrink-0 mt-0.5 cursor-grab active:cursor-grabbing text-gray-600 hover:text-gray-300 font-mono leading-none text-[12px] px-1"
                title="Drag to reorder"
                aria-label="reorder"
              >⋮⋮</span>
              <span class="font-mono text-[9px] text-gray-600 mt-1 w-4 text-right flex-shrink-0">{i() + 1}</span>
              <Show
                when={editingId() === it.id}
                fallback={
                  <div class="flex-1 min-w-0">
                    <button
                      type="button"
                      onClick={() => beginEdit(it)}
                      class="w-full text-left text-gray-300 hover:text-gray-100 leading-snug break-words min-w-0"
                      title="Click to edit"
                    >
                      {it.text}
                    </button>
                    {/* ATT1 — the images that go with this queued message. */}
                    <Show when={(it.attachments ?? []).length > 0}>
                      <AttachmentGrid attachments={it.attachments ?? []} align="left" />
                    </Show>
                  </div>
                }
              >
                <textarea
                  autofocus
                  value={editValue()}
                  rows="2"
                  onInput={(e) => setEditValue(e.currentTarget.value)}
                  onBlur={commitEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commitEdit(); }
                  }}
                  class="flex-1 bg-gray-950 border border-sky-500/40 rounded px-2 py-1 text-[12.5px] text-gray-100 resize-none focus:outline-none"
                />
              </Show>
              <Show when={it.status === 'sending'}>
                <span class="text-[10px] font-mono text-amber-300 flex-shrink-0 mt-1">sending…</span>
              </Show>
              <Show when={it.status === 'failed'}>
                <span class="text-[10px] font-mono text-red-300 flex-shrink-0 mt-1" title={it.failed_reason ?? 'dispatch failed'}>failed</span>
              </Show>
              {/* 2026-06-10 case 2 — explicit per-item controls. The
               *  drag handle on the left still works for power users;
               *  the up/down arrows give a discoverable click target.
               *  Pencil opens the inline textarea editor; ✕ removes. */}
              <div
                class="flex items-center gap-0.5 flex-shrink-0 ml-1 opacity-60 group-hover:opacity-100 transition-opacity"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => props.onMove(it.id, Math.max(0, i() - 1))}
                  disabled={i() === 0}
                  class="text-gray-500 hover:text-sky-300 disabled:opacity-30 disabled:cursor-not-allowed px-1 py-0.5 leading-none"
                  title="Move up"
                  aria-label="move up"
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 19V5M5 12l7-7 7 7" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => props.onMove(it.id, Math.min(props.items.length - 1, i() + 1))}
                  disabled={i() === props.items.length - 1}
                  class="text-gray-500 hover:text-sky-300 disabled:opacity-30 disabled:cursor-not-allowed px-1 py-0.5 leading-none"
                  title="Move down"
                  aria-label="move down"
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 5v14M5 12l7 7 7-7" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => beginEdit(it)}
                  class="text-gray-500 hover:text-amber-300 px-1 py-0.5 leading-none"
                  title="Edit"
                  aria-label="edit"
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 20h9M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => props.onDelete(it.id)}
                  class="text-gray-500 hover:text-red-300 px-1 py-0.5 leading-none"
                  title="Remove from queue"
                  aria-label="delete"
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </li>
          )}
        </For>
      </ul>
    </div>
  );
}
